import { MouseEvent, useMemo, useState } from 'react'
import { TableBody, TableCell, TableRow, Paper, IconButton, Box, Stack, Typography } from '@mui/material'
import EnhancedTable from '@common/components/Table/EnhancedTable'
import TableToolbar from './TableToolbar'
import EnhancedNavTabs from '@common/components/Tab/EnhancedNavTabs'
import {
  PaginatedResponse_StudyUserReadSchema_,
  StudyUserReadSchema,
  StudyUsersMetadata,
} from '@common/config/api/client'
import { HeadCell, Order } from '@common/components/Table/table.types'
import useUserTable from '../../hooks/useUserTable'
import { SPACING } from '@common/theme/spacing'
import { NavTabsVariant } from '@common/components/Tab/tabs.types'
import { DeleteOutlineRounded } from '@mui/icons-material'
import { SIZING } from '@common/theme/sizing'
import ConfirmationDialog from '@common/components/ConfirmationDialog/ConfirmationDialog'
import { getRoleDisplayName } from '@common/utils/roleDisplayName'
import { StudyCompanySite } from '@features/study_details/sites/hooks'

export interface Props {
  studyUsersMetadata: StudyUsersMetadata | undefined
  usersList: PaginatedResponse_StudyUserReadSchema_ | undefined
  studyCompanySites?: StudyCompanySite[]
  onSearch: (value: string) => void
  onUserTypeSelect: (user_type: string) => void
  onDeleteUser: (userId: string) => void
  searchValue?: string
  page: number
  orderBy: keyof StudyUserReadSchema
  order: Order
  onChangePage: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void
  onRequestSort: (event: MouseEvent<unknown>, property: string) => void
  onChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void
}

type UserTableSchema = StudyUserReadSchema & {
  site_information: never
  actions: never
}

const headCells: readonly HeadCell<UserTableSchema>[] = [
  { id: 'user_name', label: 'User name', disablePadding: false },
  { id: 'user_email', label: 'Email address', disablePadding: false },
  { id: 'role_name', label: 'Role', disablePadding: false },
  { id: 'site_information', label: 'Site information', disablePadding: false, disableSort: true },
  { id: 'is_verified', label: 'Verified', disablePadding: false },
  { id: 'created_by', label: 'Created by', disablePadding: false },
  { id: 'actions', label: '', disablePadding: false },
] as const

function EmptyUserTable() {
  return (
    <Stack spacing={SPACING.minSpacing} alignItems="center" sx={{ py: SPACING.spacing2xl }}>
      <Typography variant="h6">No matching user found</Typography>
      <Typography variant="body2" color="text.secondary">
        Please ensure the user's name is correct, or try a different one
      </Typography>
    </Stack>
  )
}

export default function UserTable({
  studyUsersMetadata,
  usersList,
  studyCompanySites,
  onSearch,
  searchValue,
  onUserTypeSelect,
  onDeleteUser,
  page,
  order,
  orderBy,
  onChangePage,
  onRequestSort,
  onChangeRowsPerPage,
}: Props) {
  const users = usersList?.items ?? []
  const { handleDeleteUser, getUserTypeRoutes, setSelectedUserId } = useUserTable(users, onDeleteUser)

  // Only site users belong to a company that was added to the study as a site, so every other user
  // resolves to no site information.
  const siteInformationByCompanyId = useMemo(
    () =>
      new Map((studyCompanySites ?? []).map((site) => [site.company_id, `${site.pseudo_id} - ${site.name}`] as const)),
    [studyCompanySites],
  )

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

  const handleDeleteUserClick = (event: React.MouseEvent, userId: string) => {
    event.stopPropagation()
    setSelectedUserId(userId)
    setConfirmDialogOpen(true)
  }

  const handleConfirmDelete = () => {
    handleDeleteUser()
    setConfirmDialogOpen(false)
  }

  const handleCancelDelete = () => {
    setConfirmDialogOpen(false)
  }

  const userTypeRoutes = getUserTypeRoutes(studyUsersMetadata)

  return (
    <Box component="section">
      <TableToolbar searchValue={searchValue} onSearch={onSearch} onUserTypeSelect={onUserTypeSelect} />
      <Paper sx={{ p: SPACING.paperInnerPadding }}>
        <EnhancedNavTabs routes={userTypeRoutes} variant={NavTabsVariant.SEGMENTED_CONTROLS} />
        <EnhancedTable<UserTableSchema>
          rows={users}
          headCells={headCells}
          order={order}
          orderBy={orderBy}
          selected={[]}
          rowsPerPageOptions={[10, 25, 50]}
          page={page}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          onRequestSort={onRequestSort}
          next_cursor={usersList?.pagination.next_cursor}
          previous_cursor={usersList?.pagination.previous_cursor}
          rowsPerPage={usersList?.pagination.limit ?? 50}
        >
          <TableBody>
            {users.length > 0 ? (
              users.map((user) => (
                <TableRow key={user.user_id}>
                  <TableCell>{user.user_name}</TableCell>
                  <TableCell>{user.user_email}</TableCell>
                  <TableCell>{getRoleDisplayName(user.role_name)}</TableCell>
                  <TableCell>{(user.company_id && siteInformationByCompanyId.get(user.company_id)) || ''}</TableCell>
                  <TableCell>{user.is_verified ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{user.created_by}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={(e: React.MouseEvent) => handleDeleteUserClick(e, user.user_id)}>
                      <DeleteOutlineRounded
                        sx={{ w: SIZING.iconWidth, mr: SPACING.buttonSpacing, color: 'text.secondary' }}
                      />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={headCells.length}>
                  <EmptyUserTable />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </EnhancedTable>
      </Paper>

      <ConfirmationDialog
        open={confirmDialogOpen}
        title="Delete user?"
        description="This will revoke the users access to the platform and cannot be undone."
        confirmLabel="Delete user"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </Box>
  )
}
